/* eslint-disable no-console */
import { mapKeys, omit, uniq } from "lodash";

import { v4 as uuidv4 } from "uuid";

import { BASE_URL } from "@constants/common";

import getProductDefaultConfiguration from "@helpers/products/getProductDefaultConfiguration";

import { Item } from "@typings/product/item";
import { Product } from "@typings/product/product";
import { KlaviyoEvent } from "@typings/analytics/klaviyo";

import { transformCartItemsToKlaviyo } from "./utils/transformCartItemsToKlaviyo";
import { getSubtotal, getTotal } from "./utils/getTotals";
import { transformCartItemsToUrl } from "./utils/transformCartItemsToUrl";
import { getShippingCost } from "./utils/getShippingCost";
import { transformAddressToKlaviyo } from "./utils/transformAddressToKlaviyo";
import getProductName from "@helpers/products/getProductName";

/* ===========
  Types
  ======== */
interface TrackStartedCheckoutParams {
  email: string;
  userInfo?: any;
  cartItems: Product[];
}

interface TrackPlacedOrderParams {
  email: string;
  userInfo: any;
  cartItems: Product[];
  orderId: number;
  billingAddress: any;
  shippingAddress: any;
  coupon?: string;
  discount: number;
}

interface TrackOrderedProductParams {
  cartItem: Product;
  orderId: number;
}

declare global {
  interface Window {
    _learnq: any;
    dataLayer: any;
  }
}

/* =============
  Analytic Functions
  ============ */
const dataLayer = typeof window !== "undefined" && window.dataLayer;
const learnq = typeof window !== "undefined" && window._learnq;

export const isIdentified = (): string | undefined => learnq?.isIdentified?.();

export const identify = (email: string, userInfo?: any): void => {
  const info = userInfo
    ? mapKeys(
        {
          ...userInfo,
          first_name: userInfo.firstName,
          last_name: userInfo.lastName,
        },
        (_val, key) => `$${key}`
      )
    : {};

  learnq?.push([
    "identify",
    {
      $email: email,
      ...info,
    },
  ]);
};

export const track = (event: KlaviyoEvent, data: any): void => {
  console.info(`Tracking ${event}: `, data);

  dataLayer?.push({ event, ...data });

  learnq?.push(["track", event, data]);
};

export const trackAddedToCart = (cartItems: Product[], newCartItems: Product[]): void => {
  const cartItemsURL = transformCartItemsToUrl(cartItems);
  const klaviyoCartItems = transformCartItemsToKlaviyo(cartItems);
  const klaviyoNewCartItems = transformCartItemsToKlaviyo(newCartItems);

  const cartItemsNames = uniq(cartItems.map((item) => getProductName(item)));

  const value = getSubtotal(cartItems);

  klaviyoNewCartItems.forEach((klaviyoNewCartItem) => {
    track("Added to Cart", {
      ...mapKeys(klaviyoNewCartItem, (_, key) => `AddedItem${key}`),
      $value: value,
      ItemNames: cartItemsNames,
      ItemCount: klaviyoCartItems.length,
      CartUrl: cartItemsURL,
    });
  });
};

export const trackStartedCheckout = ({
  email,
  userInfo,
  cartItems,
}: TrackStartedCheckoutParams): void => {
  identify(email, userInfo);

  const cartItemsURL = transformCartItemsToUrl(cartItems);
  const checkoutUrl = transformCartItemsToUrl(cartItems, "/checkout");
  const klaviyoCartItems = transformCartItemsToKlaviyo(cartItems);

  const cartItemsNames = uniq(cartItems.map((item) => getProductName(item)));

  const value = getSubtotal(cartItems);

  const data = {
    $event_id: uuidv4(),
    $value: value,
    ItemNames: cartItemsNames,
    CheckoutURL: checkoutUrl,
    CurrencySymbol: "&#36",
    Currency: "USD",
    extra: {
      Items: klaviyoCartItems,
      SubTotal: value,
    },
    CartUrl: cartItemsURL,
  };

  track("Started Checkout", data);
};

export const trackOrderedProduct = ({ cartItem, orderId }: TrackOrderedProductParams): void => {
  const [klaviyoCartItem] = transformCartItemsToKlaviyo([cartItem]);

  const value = getSubtotal([cartItem]);

  const data = {
    ...klaviyoCartItem,
    $event_id: `${orderId}_${klaviyoCartItem.ProductID}_${klaviyoCartItem.OfferID}`,
    $value: value,
    OrderId: orderId,
    time: new Date().toISOString(),
  };

  track("Ordered Product", data);
};

export const trackPlacedOrder = ({
  cartItems,
  orderId,
  billingAddress,
  shippingAddress,
  userInfo,
  coupon,
  discount,
  email,
}: TrackPlacedOrderParams): void => {
  identify(email, userInfo);

  const shippingCost = getShippingCost(cartItems);
  const value = getTotal(cartItems, discount, shippingCost);

  const klaviyoCartItems = transformCartItemsToKlaviyo(cartItems);
  const cartItemsNames = uniq(cartItems.map((item) => getProductName(item)));

  const phone = userInfo.phoneNumber!;

  const data = {
    $event_id: orderId,
    $value: value,
    $currency_code: "USD",
    time: new Date().toISOString(),
    ItemNames: cartItemsNames,
    IsDiscounted: !!discount,
    UsedCoupon: !!coupon,
    DiscountCode: coupon,
    DiscountValue: discount,
    extra: {
      OrderId: orderId,
      TotalShipping: shippingCost,
      TotalDiscount: discount,
      BillingAddress: transformAddressToKlaviyo({
        customer: userInfo,
        address: billingAddress,
        phone,
      }),
      ShippingAddress: transformAddressToKlaviyo({
        customer: userInfo,
        address: shippingAddress,
        phone,
      }),
      Items: klaviyoCartItems,
    },
  };

  track("Placed Order", data);

  cartItems.forEach((cartItem) =>
    trackOrderedProduct({
      cartItem,
      orderId,
    })
  );
};

export const trackViewedItem = (item: Item): void => {
  const [product] = transformCartItemsToKlaviyo([
    { item, configuration: getProductDefaultConfiguration(item) },
  ]);

  const data = { ...omit(product, ["LineTotal", "Quantity", "PackageSize"]) };

  track("Viewed Product", data);
};
